import React, { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Lender, LENDERS, getLendersWithRates } from "../services/loanService";

interface Props {
  onSelect: (lender: Lender) => Promise<void>;
  onClose: () => void;
}

// Mirrors android-app/components/LenderSelectionModal.tsx
const Detail: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex-1 px-2 py-1.5 bg-[#F9FAFB] rounded-lg">
    <div className="text-[9px] text-[#999] mb-0.5 capitalize">{label}</div>
    <div className="text-xs font-bold text-[#1A1A2E]">{value}</div>
  </div>
);

const LenderSelectionModal: React.FC<Props> = ({ onSelect, onClose }) => {
  const [lenders, setLenders] = useState(LENDERS);
  const [selected, setSelected] = useState<string | null>(null);
  const [shown, setShown] = useState(false);
  const loading = selected !== null;

  useEffect(() => {
    getLendersWithRates().then(setLenders);
    const t = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const tap = async (l: Lender) => {
    setSelected(l.platform);
    try {
      await onSelect(l);
    } finally {
      setSelected(null);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[60] flex items-end justify-center bg-black/50 transition-opacity duration-300 ${shown ? "opacity-100" : "opacity-0"}`}
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      <div
        className={`bg-white w-full max-w-[480px] h-[95%] rounded-t-[32px] flex flex-col overflow-hidden transition-transform duration-[350ms] ease-[cubic-bezier(0.23,1,0.32,1)] ${shown ? "translate-y-0" : "translate-y-[100px]"}`}
      >
        <div className="relative">
          <div className="px-4 pt-[18px] pb-3" style={{ background: "linear-gradient(to bottom, #F5FCFF, #FFFFFF)" }}>
            <h2 className="text-[22px] font-bold leading-7 text-[#1A1A2E] mb-1.5">
              Keep Your Investments,<br />Get Instant Funds
            </h2>
            <p className="text-xs leading-[18px] text-[#666]">
              Pledge your mutual funds and get a loan without selling them. Quick, easy, and secure.
            </p>
          </div>
          <button onClick={onClose} disabled={loading} aria-label="Close" className="absolute top-3 right-4 w-9 h-9 flex items-center justify-center">
            <XMarkIcon className="h-6 w-6 text-[#1A1A2E]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col gap-2.5 px-3 py-2">
          {lenders.map((l) => {
            const isSel = selected === l.platform;
            return (
              <div
                key={l.platform}
                className={`rounded-2xl p-3 bg-white shrink-0 ${isSel ? "border-2 border-[#2E8B57] shadow-md" : "border border-[#E8E8E8] shadow-sm"}`}
              >
                <div className="flex items-start gap-2.5 mb-[7px]">
                  <img src={l.logo} alt={l.displayName} className="w-[50px] h-[50px] object-contain"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  <div className="flex-1 pt-0.5 self-center text-[13px] font-bold text-[#1A1A2E]">{l.displayName}</div>
                </div>
                <div className="mb-2.5">
                  <div className="flex gap-2 mb-2">
                    <Detail label="Interest rate" value={l.details.interestRate} />
                    <Detail label="Foreclosure Charges" value={l.details.foreclosureCharges} />
                  </div>
                  <div className="flex gap-2">
                    <Detail label="Tenure" value={l.details.tenure} />
                    <Detail label="Processing Fee" value={l.details.processingFee} />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    disabled={loading}
                    onClick={() => tap(l)}
                    className={`bg-[#129481] text-white text-sm font-bold tracking-[0.2px] rounded-lg px-6 py-[9px] min-w-[110px] flex justify-center shadow-sm active:scale-95 transition-transform ${isSel ? "opacity-80" : ""}`}
                  >
                    {isSel ? <span className="h-5 w-5 rounded-full border-2 border-white border-t-transparent animate-spin" /> : "Apply Now"}
                  </button>
                </div>
              </div>
            );
          })}
          <div className="h-2.5 shrink-0" />
        </div>

        <div className="px-3 py-3 bg-white border-t border-[#F0F0F0]">
          <button onClick={onClose} disabled={loading}
            className="w-full py-[11px] rounded-[10px] bg-[#F5F5F5] border border-[#E8E8E8] text-[15px] font-semibold text-[#666]">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default LenderSelectionModal;
